import React, { ReactElement } from 'react';

import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import { FilterAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import Controller, { OnClick, OnDynamicContentRender, OnMount, OnSelectItem } from '@philips-emr/tws-core/lib/client';
import { MountEvent, RenderEvent, SelectItemEvent } from '@philips-emr/tws-core/lib/component/types';

import { ActionPlanAliases as Aliases, FragmentNames } from '../../codegen';
import { Route } from '../../route';
import BaseController from '../BaseController';

enum ActionPlanStatus {
  TEMPORARY = '1',
  AWAITING_APROVAL = '2',
  ONGOING = '3',
  CONCLUDED = '4',
  DENIED = '5',
  CANCELED = '6',
  REQUESTED_CLOSING = '7',
  AWAITING_TECHNICAL_VISIT = '8',
  AWAITING_CLOSING = '9',
}

interface ActionPlanData {
  ieStatus: ActionPlanStatus,
  dsStatus: string,
  nrSeqActionPlan: number,
  nrSeqProvider: number,
  nrSeqActionPlanItem: number,
  nrSeqActionPlanHistory: number,
  statusActionPlan: ActionPlanStatus,
  ieAttachment: string,
  dsCancelReason: string,
  effectivity: string,
}

@Controller({ fragment: FragmentNames.ACTION_PLAN })
class ActionPlanController extends BaseController {

  @OnMount(Aliases.FILTER)
  onFilterMount(event: MountEvent): void {
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const api = event.target as FilterAPI;

    api.setData({
      nrSeqProvider,
    });
  }

  @OnClick(Aliases.ACTION_GENERATE)
  onClickGenerate(): void {
    this.historyService.push(Route.ACTION_PLAN_NEW);
  }

  @OnDynamicContentRender(Aliases.CARD_ACTION_PLAN)
  onRenderStatusActionPlan(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus, dsStatus } = data as ActionPlanData;
    if (ieStatus) {
      const content = (): ReactElement => (getStampStatusActionPlan(ieStatus, dsStatus));
      render(content);
    }
  }

  @OnSelectItem(Aliases.DECK_ACTION_PLAN)
  onSelectItemBeneficiarios(event: SelectItemEvent<ActionPlanData>): void {
    const { nrSeqActionPlan } = event.item;
    this.historyService.push(Route.ACTION_PLAN_DETAILS, { nrSeqActionPlan });
  }

}

function getStampStatusActionPlan(ieStatus: ActionPlanStatus, label: string): ReactElement {
  let stampProps = null;
  const icon = {
    [ActionPlanStatus.TEMPORARY]: { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CLOCK, label },
    [ActionPlanStatus.AWAITING_APROVAL]: { className: 'c-stamp-custom-color-purple', icon: StampIcon.CLOCK, label },
    [ActionPlanStatus.ONGOING]: { color: Color.SAFETY_BLUE, icon: StampIcon.CLOCK, label },
    [ActionPlanStatus.CONCLUDED]: { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label },
    [ActionPlanStatus.DENIED]: { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label },
    [ActionPlanStatus.CANCELED]: { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CANCELED, label },
    [ActionPlanStatus.REQUESTED_CLOSING]: { color: Color.SAFETY_YELLOW, icon: StampIcon.CHECK, label },
    [ActionPlanStatus.AWAITING_TECHNICAL_VISIT]: { color: Color.SAFETY_ORANGE, icon: StampIcon.CLOCK, label },
    [ActionPlanStatus.AWAITING_CLOSING]: { color: Color.SAFETY_GREEN, icon: StampIcon.CLOCK, label },
  };

  stampProps = icon[ieStatus];
  return <Stamp {...stampProps} />;
}

export {
  ActionPlanController,
  getStampStatusActionPlan,
  ActionPlanData,
  ActionPlanStatus,
};
